import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, Grid, Text } from 'src/packages/core';
import { DarkTheme, LightTheme, ThemeType, useTheme } from 'src/packages/theme';
import { CheckedIcon } from 'src/packages/icons';

import { Locale } from '../Header.types';
import { LanguageCurrencyItemWrapper } from '../Header.styles';

interface GlobeIconPopoverContentProps {
  locale: Locale;
  theme: ThemeType;
}

export const GlobeIconPopoverContent = ({ locale, theme }: GlobeIconPopoverContentProps) => {
  const { t } = useTranslation();
  const { languages, currencies, language, currency, changeSettings } = locale;

  const contextTheme = useTheme();

  const defaultTheme = useMemo(() => {
    return theme
      ? theme === 'light'
        ? { ...LightTheme, rtl: contextTheme.rtl }
        : { ...DarkTheme, rtl: contextTheme.rtl }
      : contextTheme;
  }, [contextTheme, theme]);

  return useMemo(
    () => (
      <Grid
        width={'100%'}
        gridTemplateColumns={'auto auto'}
        gridTemplateRows={'1fr'}
        gap={2}
        p={2}
        backgroundColor={defaultTheme.colors.background.secondary.BG3}
      >
        <Flex width={'100%'} flexDirection={'column'}>
          <Text
            variant={'p3'}
            text={t('language')}
            color={
              theme === 'light' ? LightTheme.colors.text.tertiary : DarkTheme.colors.text.tertiary
            }
            py={3}
            px={5}
          />

          <Flex width={'100%'} flexDirection={'column'} overflow={'auto'} maxHeight={'250px'}>
            {languages.map((item, index) => {
              return (
                <LanguageCurrencyItemWrapper
                  bgHoverColor={defaultTheme.colors.background.secondary.BG4}
                  key={index}
                  minWidth={'181px'}
                  onClick={() => {
                    changeSettings && changeSettings('lang', item.value);
                  }}
                >
                  <Text
                    variant={'p1'}
                    text={item.label as string}
                    color={
                      theme === 'light'
                        ? LightTheme.colors.text.primary
                        : DarkTheme.colors.text.primary
                    }
                  />
                  {language === item.value && (
                    <CheckedIcon
                      size={20}
                      color={
                        theme === 'light'
                          ? LightTheme.colors.text.primary
                          : DarkTheme.colors.palette.white
                      }
                    />
                  )}
                </LanguageCurrencyItemWrapper>
              );
            })}
          </Flex>
        </Flex>

        <Flex width={'100%'} flexDirection={'column'}>
          <Text
            variant={'p3'}
            text={t('currency')}
            color={
              theme === 'light' ? LightTheme.colors.text.tertiary : DarkTheme.colors.text.tertiary
            }
            py={3}
            px={5}
          />

          <Flex width={'100%'} flexDirection={'column'} overflow={'auto'} maxHeight={'250px'}>
            {currencies.map((item, index) => {
              const val = item?.value ? item.value.toString() : '';

              return (
                <LanguageCurrencyItemWrapper
                  key={index}
                  minWidth={'125px'}
                  bgHoverColor={defaultTheme.colors.background.secondary.BG4}
                  onClick={() => {
                    changeSettings && changeSettings('unit', val.toLowerCase());
                  }}
                >
                  <Text
                    variant={'p1'}
                    text={item.label as string}
                    color={
                      theme === 'light'
                        ? LightTheme.colors.text.primary
                        : DarkTheme.colors.text.primary
                    }
                  />
                  {currency?.toLowerCase() === val.toLowerCase() && (
                    <CheckedIcon
                      size={20}
                      color={
                        theme === 'light'
                          ? LightTheme.colors.text.primary
                          : DarkTheme.colors.palette.white
                      }
                    />
                  )}
                </LanguageCurrencyItemWrapper>
              );
            })}
          </Flex>
        </Flex>
      </Grid>
    ),
    [locale, theme]
  );
};
